/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  TouchableOpacity,
  Linking,
  ScrollView,
  Dimensions,
} from 'react-native';
import { isWeb, isMobileWeb } from '@global/screens';
import Colors from '@global/colors';
import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import Input from '@components/form/Input';
import * as Layout from '@components/screen/Layout';
import { Loader } from '@components/screen/Layout';

import { MsCatalog } from '@api/ms-catalog';

const dimension = Dimensions.get('screen');

const openPdf = (url) => {
  if (!url) {
    return;
  }
  if (isWeb) {
    window.open(url, '_blank');
  } else {
    Linking.openURL(url);
  }
};

const GuideBookRow = ({ item, expanded, expandLoading, pdfs, onToggle }) => (
  <View
    style={{
      borderBottomWidth: 1,
      borderBottomColor: Colors.grey0,
    }}>
    <TouchableOpacity
      testID={`guide-book-row-${item._id}`}
      activeOpacity={0.7}
      onPress={() => onToggle(item)}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 16,
        paddingHorizontal: 20,
      }}>
      <Image
        source={require('@assets/img/icons/folder-guidebook.svg')}
        style={{ width: 22, height: 22, marginRight: 14 }}
        width={22}
        height={22}
        svg={true}
      />
      <View style={{ flex: 1 }}>
        <SociollaText type="medium">{item.title}</SociollaText>
        <SociollaText style={{ fontSize: 12, color: Colors.grey1 }}>
          {item.fileCount || 0} file{item.fileCount == 1 ? '' : 's'}
        </SociollaText>
      </View>
      <Image
        source={
          expanded
            ? require('@assets/img/icons/arrow-down-grey.svg')
            : require('@assets/img/icons/arrow-right-grey.svg')
        }
        style={{ width: 12, height: 12 }}
        width={12}
        height={12}
        svg={true}
      />
    </TouchableOpacity>
    {expanded ? (
      <View style={{ paddingBottom: 8 }}>
        {expandLoading ? (
          <View style={{ padding: 12 }}>
            <Loader />
          </View>
        ) : pdfs.length ? (
          pdfs.map((pdf, idx) => (
            <TouchableOpacity
              testID={`guide-book-pdf-${pdf._id || idx}`}
              key={pdf._id || idx}
              activeOpacity={0.7}
              onPress={() => openPdf(pdf.url)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 10,
                paddingHorizontal: 20,
                paddingLeft: 56,
              }}>
              <Image
                source={require('@assets/img/icons/pdf-guidebook.svg')}
                style={{ width: 18, height: 18, marginRight: 10 }}
                width={18}
                height={18}
                svg={true}
              />
              <SociollaText
                numberOfLines={1}
                ellipsizeMode="tail"
                style={{ flex: 1, fontSize: 13 }}>
                {pdf.description || `pdf-section-${idx + 1}`}
              </SociollaText>
              <Image
                source={require('@assets/img/icons/eye-guidebook.svg')}
                style={{ width: 16, height: 16 }}
                width={16}
                height={16}
                svg={true}
              />
            </TouchableOpacity>
          ))
        ) : (
          <View style={{ paddingHorizontal: 56, paddingVertical: 8 }}>
            <SociollaText style={{ fontSize: 12, color: Colors.grey1 }}>
              No files found.
            </SociollaText>
          </View>
        )}
      </View>
    ) : (
      false
    )}
  </View>
);

const GuideBookScreen = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandLoading, setExpandLoading] = useState(false);
  const [pdfsById, setPdfsById] = useState({});
  const debounceRef = useRef(null);

  const fetchGuideBooks = async (title) => {
    setLoading(true);
    try {
      // ms-b2b-catalog's shared query-parser middleware caps unregistered endpoints at 50
      // (see sociolla-core/lib/middlewares/qsParserConfig.js ServiceFallbackLimits) — a
      // higher value throws "query parameter 'limit' is out of range".
      const params = { params: { limit: 50, skip: 0, filter: {} } };
      if (title) {
        params.params.filter.title = { $regex: title, $options: 'i' };
      }
      const response = await MsCatalog.getGuideBooks(params);
      setItems(response?.data?.data || []);
    } catch (e) {
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGuideBooks();
    return () => clearTimeout(debounceRef.current);
  }, []);

  const isFirstFocusRef = useRef(true);
  useFocusEffect(
    useCallback(() => {
      if (!isFirstFocusRef.current) {
        fetchGuideBooks(searchValue.trim());
      }
      isFirstFocusRef.current = false;
    }, [searchValue]),
  );

  const onChangeSearch = (value) => {
    setSearchValue(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!value || value.trim().length >= 2) {
        fetchGuideBooks(value.trim());
      }
    }, 400);
  };

  const onToggle = async (item) => {
    if (expandedId === item._id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(item._id);
    if (!pdfsById[item._id]) {
      setExpandLoading(true);
      const response = await MsCatalog.getGuideBookDetail(item._id);
      setPdfsById((prev) => ({
        ...prev,
        [item._id]: response?.data?.data?.pdfs || [],
      }));
      setExpandLoading(false);
    }
  };

  return (
    <Layout.Container>
      {isWeb ? (
        <Layout.Back
          testID="guide-book-back-btn"
          title="Dashboard"
          onCallback={() => navigation.navigate('HomeNavigator')}
        />
      ) : (
        false
      )}
      {isWeb ? <Layout.Title title="Guide Book" /> : false}
      <View
        style={
          isWeb
            ? {
                backgroundColor: Colors.white,
                borderRadius: 8,
                maxWidth: 804,
                minHeight: 512,
                paddingTop: 24,
                paddingBottom: 24,
                overflow: 'hidden',
              }
            : { flex: 1 }
        }>
        <View
          style={
            isWeb
              ? { padding: 16 }
              : { paddingHorizontal: 16, paddingTop: 20, paddingBottom: 8 }
          }>
          <Input
            testID="guide-book-search"
            placeholder="Search guide books"
            value={searchValue}
            onChangeText={onChangeSearch}
            leftIcon={
              <Image
                source={
                  searchValue
                    ? require('@assets/img/icons/search-guidebook-active.svg')
                    : require('@assets/img/icons/search-guidebook-empty.svg')
                }
                style={{ width: 16, height: 16 }}
                width={16}
                height={16}
                svg={true}
              />
            }
          />
        </View>
        {loading ? (
          <View style={{ padding: 30, alignItems: 'center' }}>
            <Loader />
          </View>
        ) : items.length ? (
          <ScrollView
            style={isMobileWeb ? { height: dimension.height - 55 } : {}}>
            {items.map((item) => (
              <GuideBookRow
                key={item._id}
                item={item}
                expanded={expandedId === item._id}
                expandLoading={expandLoading && expandedId === item._id}
                pdfs={pdfsById[item._id] || []}
                onToggle={onToggle}
              />
            ))}
          </ScrollView>
        ) : (
          <View
            style={{
              flex: 1,
              padding: 30,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Image
              source={require('@assets/img/icons/search-guidebook-empty.svg')}
              style={{ width: 43, height: 43, marginBottom: 12 }}
              width={43}
              height={43}
              svg={true}
            />
            <SociollaText type="medium" style={{ marginBottom: 4 }}>
              No results found.
            </SociollaText>
            <SociollaText style={{ fontSize: 12, color: Colors.grey1 }}>
              Try using different keywords.
            </SociollaText>
          </View>
        )}
      </View>
    </Layout.Container>
  );
};

export default GuideBookScreen;
